/*
 * Settings. Appearance belongs to the shell and is implemented here; profile, notifications and
 * security are the identity and notification areas' and mount into the same frame through their own
 * files (spec 12 §4.1, §4.5).
 */

export const en = {
	"settings.title": "Settings",
	"settings.nav.appearance": "Appearance",
	"settings.nav.messages": "Messages",
	"settings.nav.security": "Security",

	// ---- K-05: the frame (VISUAL.md §7.12, §7.13) ----
	//
	// One line under the page's heading per tab: the strip names five things and says what none of
	// them is for, so each tab says it itself. And one acknowledgement for the whole area — the
	// sentence a form keeps beside its Save button and the word a switch that writes on change has
	// said to a screen reader since `lib/useSaved.ts` (D-10 P9).
	"settings.lead.appearance": "How teamBase looks on this device.",
	"settings.lead.messages": "What the message box does.",
	"settings.lead.notifications": "What reaches you, and how.",
	"settings.lead.profile": "What the others see of you.",
	"settings.lead.security": "Your sessions and trusted devices.",
	"settings.saved": "Saved.",

	"settings.appearance.theme": "Theme",
	"settings.appearance.theme.lead":
		"Each card shows what it does: the navigation column, the page and the accent. This is a decision of this browser.",
	"settings.appearance.theme.system": "Follow the system",
	"settings.appearance.theme.light": "Light",
	"settings.appearance.theme.dark": "Dark",
	"settings.appearance.announcements": "Announcements",
	"settings.appearance.announcements.lead":
		"What a screen reader is told about while you are looking at something else.",
	"settings.appearance.announce": "Announce new messages",
	"settings.appearance.announce.hint":
		"Screen readers say when something arrives in the open conversation.",

	// ---- S-1: the composer (A-08 §5) ----
	//
	// Three of these four settings have been registered, validated and read by the composer since
	// spec 12 and were unreachable by a person: the only way to change one was a PUT from a
	// console. The undo window's copy says what it costs — every message waits that long before
	// its author sees it — because "Off" is the shipped answer and switching it on is a trade.
	// The two groups of the Messages tab: what the box does while you write, and the window a sent
	// message waits in. The undo window's own sentence is the group's lead — it says what the setting
	// costs, which is the thing to read before choosing (K-05).
	"settings.composer.writing": "While you write",
	"settings.composer.writing.lead": "What the message box does under your hands.",
	"settings.composer.enterSends": "Enter sends the message",
	"settings.composer.enterSends.hint":
		"When this is off the two swap: Shift+Enter sends and Enter starts a new line. Inside a code block Enter always starts a new line.",
	"settings.composer.previewOpen": "Show the preview while writing",
	"settings.composer.previewOpen.hint":
		"The preview pane starts open in every message box, instead of being opened when you want it.",
	"settings.composer.sourceMode": "Edit as markdown",
	"settings.composer.sourceMode.hint":
		"Write the markdown yourself instead of seeing the formatting as you type. The message box switches the same setting, from the button at the right-hand end of its toolbar.",
	"settings.composer.undoSend": "Wait before sending",
	"settings.composer.undoSend.hint":
		"A message you have sent stays on this device for this long, and Undo in the row takes it back without anybody seeing it. It is also how long you wait for your own message.",
	"settings.composer.undoSend.off": "Off — send at once",
	"settings.composer.undoSend.seconds": "{seconds} seconds",

	// ---- Q-09 (X-A3 C8) ----
	//
	// One sentence for "not built", wherever a tab of this area has nothing behind it yet: two
	// wordings of the same promise read as two different states.
	"settings.pending": "Not built yet. This arrives in a later stage.",
	// A page a module of this build contributes, at the address of a reader who may not use that
	// module: it exists and is withheld, so it is not promised for a later stage (I1).
	"settings.unavailable": "This page is not available to you.",
	// ---- V-06: the profile (X-A4 N7) ----
	//
	// The fields have existed in the schema, in `ProfilePatch` and in the matrix since spec 01 and a
	// person could reach none of them: `/settings/profile` answered "not built yet" (X-A4 N7). The
	// copy says what each field is *for*, because that is what decides whether somebody fills it in —
	// a title nobody sees is not worth typing.
	"settings.nav.profile": "Profile",
	"settings.profile.name": "Your name",
	"settings.profile.name.lead": "What a message, a row and a mention show of you.",
	"settings.profile.displayName": "Display name",
	"settings.profile.displayName.hint":
		"The name on every message, every row and every mention. Up to 64 characters.",
	"settings.profile.fullName": "Full name",
	"settings.profile.fullName.hint": "Only your profile shows it. Up to 120 characters.",
	"settings.profile.jobTitle": "Title",
	"settings.profile.jobTitle.hint":
		"What you do here — your profile shows it under your name. Up to 64 characters.",
	"settings.profile.pronouns": "Pronouns",
	"settings.profile.pronouns.hint":
		"For example she/her, he/him or they/them. Up to 64 characters.",
	"settings.profile.locale": "Language and time",
	"settings.profile.locale.lead": "Where you are, and which language teamBase writes to you in.",
	"settings.profile.timezone": "Time zone",
	"settings.profile.timezone.hint":
		"Your own times are shown in this zone, and your profile tells the others what o’clock it is where you are.",
	"settings.profile.language": "Language",
	"settings.profile.language.hint":
		"The language of the e-mails teamBase sends you. The interface itself is English.",
	"settings.profile.language.de": "German",
	"settings.profile.language.en": "English",
	// The handle is an address, so it stands behind a disclosure rather than in the run of fields: a
	// person who came to fix their title should not change it by accident.
	"settings.profile.handle.disclosure": "Change your handle",
	"settings.profile.handle": "Handle",
	"settings.profile.handle.warning":
		"Your handle is your address: @{handle} mentions you and /d/{handle} is the link to your direct conversation. Change it and every link to the old one that anybody has pasted anywhere stops working.",
	"settings.profile.handle.hint":
		"Two to 32 characters: lower-case letters, digits, dots, dashes and underscores, starting with a letter or a digit.",

	// The picture. There is no cropper: the upload keeps the file as it is and every avatar is drawn
	// with `object-fit: cover`, which a rounded square forgives (X-A4 N7 half B).
	"settings.profile.picture": "Picture",
	"settings.profile.picture.hint":
		"A square picture reads best — anything else is cropped to its middle.",
	"settings.profile.picture.change": "Change picture",
	"settings.profile.picture.drop": "Press the picture to choose a file, or drop one on it.",
	"settings.profile.picture.remove": "Remove the picture",
	"settings.profile.picture.uploading": "Uploading …",

	// ---- V-05: notifications (X-A4 N8, D5) ----
	//
	// Five preferences that have been registered, validated, served and announced since spec 09 and
	// that nobody could reach, one of them shipped switched on while the browser had never been asked
	// for permission. Every sentence here is written to be *true*: what the browser answered, what the
	// delay is used for, and that a direct message never carries its text into a mail whatever the
	// content switch says.
	//
	// The three `notify.desktop.*` keys are the text of the banner itself rather than of this screen.
	// The notification area has no catalogue file of its own — `core/i18n/index.ts` lists one per area
	// and is the integrator's — so they live with the screen that switches them on; moving them into a
	// `core/notify/i18n/en.ts` is a two-line hunk, and V-05's report carries it.
	"settings.nav.notifications": "Notifications",

	"settings.notifications.device": "On this device",
	"settings.notifications.device.lead":
		"Both of these are decisions of this browser. Another browser, or another computer, asks you again.",
	"settings.notifications.desktop": "Show desktop notifications",
	"settings.notifications.desktop.granted":
		"This browser allows them. You see one when something is addressed to you and you are looking somewhere else.",
	"settings.notifications.desktop.denied":
		"Your browser has blocked notifications for this site — allow them in the address bar to switch this on.",
	"settings.notifications.desktop.default":
		"Your browser has not been asked yet. Switching this on asks it, and nothing is shown until you allow it.",
	"settings.notifications.desktop.dismissed":
		"Your browser did not answer, so nothing was switched on. Use the switch again and choose Allow.",
	"settings.notifications.desktop.unsupported": "This browser cannot show notifications.",
	"settings.notifications.sound": "Play a sound",
	"settings.notifications.sound.hint":
		"A short sound with a desktop notification. Nothing sounds while you are looking at the window.",

	"settings.notifications.email": "By e-mail",
	"settings.notifications.email.lead":
		"The fallback for what you did not see: teamBase waits, and mails you only if you have still not read it.",
	"settings.notifications.email.policy":
		"Send me an e-mail if I have not seen a mention or a direct message",
	"settings.notifications.email.policy.hint":
		"Mentions and direct messages only. A message you read in time is never mailed.",
	"settings.notifications.email.delay": "Wait this long first",
	"settings.notifications.email.delay.hint":
		"How long you are given to read it yourself. Used only while the switch above is on.",
	"settings.notifications.email.delay.minutes": "{minutes} minutes",
	"settings.notifications.email.delay.hour": "1 hour",
	"settings.notifications.email.delay.hours": "{hours} hours",
	"settings.notifications.email.content": "Include the message text in the mail",
	"settings.notifications.email.content.hint":
		"With this off the mail only says that something is waiting for you here. The text of a direct message never leaves teamBase either way.",

	"settings.notifications.perConversation": "Per conversation",
	"settings.notifications.perConversation.lead":
		"What you have changed for one conversation and forgotten. Muting a conversation, and choosing what it tells you about, happens in its own menu in the sidebar.",
	"settings.notifications.perConversation.caption":
		"Conversations you have muted, or whose notifications you have changed, and how to put them back",
	"settings.notifications.perConversation.column.conversation": "Conversation",
	"settings.notifications.perConversation.column.state": "Changed to",
	"settings.notifications.perConversation.column.action": "Put it back",
	"settings.notifications.perConversation.muted": "Muted",
	"settings.notifications.perConversation.level.all": "All messages",
	"settings.notifications.perConversation.level.mention": "Only mentions",
	"settings.notifications.perConversation.level.none": "Nothing",
	"settings.notifications.perConversation.unmute": "Unmute",
	// What the level goes back to depends on the kind of conversation — a channel tells you about
	// mentions, a direct conversation about everything — so the button names the default and not a
	// level, and the same word works for both.
	"settings.notifications.perConversation.restoreLevel": "Back to the default",
	"settings.notifications.perConversation.empty":
		"Nothing is changed: every conversation notifies you the way you found it.",

	"notify.desktop.body.from": "A new message from {author}.",
	"notify.desktop.body.direct": "Sent you a direct message.",
	"notify.desktop.body.unknown": "Something new for you.",
	"notify.desktop.title.unknown": "teamBase",

	// ---- V-09: the density (X-A4 N17, X-A3 A7) ----
	//
	// The two words describe a picture, so the screen shows the picture: two message lines in the
	// rhythm that is switched on. The hint says what compact does *not* do, because the one worry a
	// person has about a denser list is that the things they click get smaller with it.
	"settings.appearance.density": "Density",
	"settings.appearance.density.comfortable": "Comfortable",
	"settings.appearance.density.compact": "Compact",
	"settings.appearance.density.hint":
		"Compact closes the space between messages and puts the name on the message’s own line. Buttons keep their size.",
	"settings.appearance.density.line1": "Shall we move the stand-up to ten?",
	"settings.appearance.density.line2": "Ten suits me.",
} as const satisfies Record<string, string>
